<template>
    <form class="flex w-full flex-col gap-5" @submit.prevent="emitSubmit">
        <div class="flex items-end gap-2">
            <div class="flex flex-1 flex-col">
                <label :for="fromId" class="mb-1 block text-xs font-semibold text-theme-toolbar-icon">
                    {{ localizations[lang].print.pageFrom }}
                </label>
                <input
                    :id="fromId"
                    v-model.number="pageFrom"
                    type="number"
                    min="1"
                    inputmode="numeric"
                    class="w-full rounded-lg border border-theme-border bg-theme-bg p-2 text-sm text-theme-text outline-none focus:border-theme-accent focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2"
                    :aria-invalid="hasError"
                    :aria-describedby="hasError ? errorId : null"
                />
            </div>
            <span aria-hidden="true" class="pb-2 text-theme-toolbar-icon">–</span>
            <div class="flex flex-1 flex-col">
                <label :for="toId" class="mb-1 block text-xs font-semibold text-theme-toolbar-icon">
                    {{ localizations[lang].print.pageTo }}
                </label>
                <input
                    :id="toId"
                    v-model.number="pageTo"
                    type="number"
                    min="1"
                    inputmode="numeric"
                    class="w-full rounded-lg border border-theme-border bg-theme-bg p-2 text-sm text-theme-text outline-none focus:border-theme-accent focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2"
                    :aria-invalid="hasError"
                    :aria-describedby="hasError ? errorId : null"
                />
            </div>
        </div>

        <p v-if="hasError" :id="errorId" role="alert" class="text-xs text-theme-text">
            {{ errorMessage }}
        </p>

        <BaseButton
            variant="primary"
            :disabled="!isValid"
            :aria-describedby="hasError ? errorId : null"
            @click="emitSubmit"
        >
            {{ localizations[lang].print.action }}
        </BaseButton>
    </form>
</template>

<script>
import { mapState } from 'vuex';
import BaseButton from '../Components/buttons/BaseButton.vue';
import { validatePrintRange } from '_shared_/js/print/validatePrintRange';

export default {
    name: 'PrintRangeForm',

    components: {
        BaseButton,
    },

    props: {
        totalBookPages: {
            type: Number,
            required: true,
        },
        printLimitPerJob: {
            type: Number,
            required: true,
        },
        remainingPages: {
            type: Number,
            required: true,
        },
        printedPages: {
            type: Array,
            default: () => [],
        },
    },

    emits: ['submit'],

    data() {
        return {
            pageFrom: null,
            pageTo: null,
        };
    },

    computed: {
        ...mapState(['localizations', 'lang']),
        fromId() {
            return `print-page-from-${this._uid}`;
        },
        toId() {
            return `print-page-to-${this._uid}`;
        },
        errorId() {
            return `print-range-error-${this._uid}`;
        },
        hasBothPages() {
            return Number.isFinite(this.pageFrom) && Number.isFinite(this.pageTo);
        },
        validation() {
            return validatePrintRange(
                { pageFrom: this.pageFrom, pageTo: this.pageTo },
                {
                    totalBookPages: this.totalBookPages,
                    printLimitPerJob: this.printLimitPerJob,
                    remainingPages: this.remainingPages,
                    printedPages: this.printedPages,
                }
            );
        },
        isValid() {
            // validatePrintRange already rejects incomplete/non-integer input, so its
            // isValid implies both pages are present — no need to re-check hasBothPages.
            return this.validation.isValid;
        },
        hasError() {
            // Gate on hasBothPages so the error only shows once both fields are filled,
            // not mid-typing when only one bound is entered.
            return this.hasBothPages && Boolean(this.validation.errorKey);
        },
        errorMessage() {
            if (!this.hasError) return '';
            return this.localizations[this.lang].print.errors[this.validation.errorKey]
                .replace('{limit}', this.printLimitPerJob)
                .replace('{remaining}', this.remainingPages);
        },
    },

    methods: {
        emitSubmit() {
            if (!this.isValid) return;
            this.$emit('submit', { pageFrom: this.pageFrom, pageTo: this.pageTo });
        },
    },
};
</script>
